// Long-form policy reader — renders a Block[] (headings, paragraphs,
// callouts, lists, tables, contact card) with **bold** inline support
// and a sticky right-hand table of contents built from the h2s.

import React from "react";
import type { Block } from "./content";

function slug(text: string): string {
  return (
    "sec-" +
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
  );
}

function renderInline(text: string): React.ReactNode {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
}

function ContactCard() {
  return (
    <div className="doc-contact">
      <div className="doc-contact-name">UAB Imir Lab</div>
      <div className="doc-contact-row">
        Registered office · V. Nagevičiaus 3g, Vilnius, Lithuania
      </div>
      <div className="doc-contact-row mono">Company code 307742103</div>
      <div className="doc-contact-row">
        Email · <a href="mailto:team@condense.chat">team@condense.chat</a>
      </div>
    </div>
  );
}

function renderBlock(b: Block, i: number): React.ReactNode {
  switch (b.kind) {
    case "h1":
      return (
        <h1 key={i} className="doc-h1">
          {b.text}
        </h1>
      );
    case "meta":
      return (
        <div key={i} className="doc-meta mono">
          {b.text}
        </div>
      );
    case "h2":
      return (
        <h2 key={i} className="doc-h2" id={slug(b.text)}>
          {b.text}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="doc-h3">
          {b.text}
        </h3>
      );
    case "p":
      return (
        <p key={i} className="doc-p">
          {renderInline(b.text)}
        </p>
      );
    case "callout":
      return (
        <div key={i} className="doc-callout">
          <span className="doc-callout-mark">!</span>
          <span>{renderInline(b.text)}</span>
        </div>
      );
    case "ul-item":
      return (
        <div key={i} className="doc-li">
          <span className="doc-li-mark mono">·</span>
          <span>{renderInline(b.text)}</span>
        </div>
      );
    case "table":
      return (
        <div key={i} className="doc-table-wrap">
          <table className="doc-table">
            <thead>
              <tr>
                {b.headers.map((h, j) => (
                  <th key={j}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "contact":
      return <ContactCard key={i} />;
    default:
      return null;
  }
}

export function DocumentReader({
  blocks,
  onBack,
}: {
  blocks: Block[];
  onBack: () => void;
}) {
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const sections = blocks.flatMap((b) =>
    b.kind === "h2" ? [{ id: slug(b.text), label: b.text }] : [],
  );

  return (
    <div className="fade-in doc-shell">
      <button type="button" className="doc-back" onClick={onBack}>
        <span aria-hidden="true">←</span> Back to Terms &amp; Conditions
      </button>
      <div className="doc-layout">
        <article className="doc-article">{blocks.map(renderBlock)}</article>
        <aside className="doc-toc">
          <div className="doc-toc-label">On this page</div>
          {sections.map((s) => (
            <a key={s.id} href={"#" + s.id} className="doc-toc-link">
              {s.label}
            </a>
          ))}
        </aside>
      </div>
    </div>
  );
}
